// Centralised "WhatsApp-style" notification helpers.
// Each helper is fail-soft — no permission / no audio context → silent.
//
// All settings are per-device (localStorage).

const LS_KEY = "gtw_chat_notif_prefs";

const DEFAULT_PREFS = {
    sound: true,                  // global ping enable
    vibrate: true,                // mobile vibration
    quiet_start: "22:00",         // 24h "HH:MM"
    quiet_end: "07:00",
    quiet_hours_enabled: false,
};

export function getNotifPrefs() {
    try {
        const raw = localStorage.getItem(LS_KEY);
        if (!raw) return { ...DEFAULT_PREFS };
        return { ...DEFAULT_PREFS, ...JSON.parse(raw) };
    } catch {
        return { ...DEFAULT_PREFS };
    }
}

export function setNotifPrefs(patch) {
    const next = { ...getNotifPrefs(), ...patch };
    try { localStorage.setItem(LS_KEY, JSON.stringify(next)); } catch { /* swallow */ }
    return next;
}

function isInQuietHours(prefs) {
    if (!prefs.quiet_hours_enabled) return false;
    const now = new Date();
    const [sh, sm] = (prefs.quiet_start || "22:00").split(":").map(Number);
    const [eh, em] = (prefs.quiet_end || "07:00").split(":").map(Number);
    const cur = now.getHours() * 60 + now.getMinutes();
    const s = sh * 60 + sm, e = eh * 60 + em;
    // Spans midnight if start > end (e.g. 22:00 → 07:00)
    return s > e ? (cur >= s || cur < e) : (cur >= s && cur < e);
}

// ── Audio: 3 distinct tones generated via Web Audio API (no asset files) ──
//   • client      → 2-tone, brighter, slightly louder (priority)
//   • document    → low + high "ding" — different timbre so users recognise docs
//   • general     → single soft tone for staff/vendor/team
let _audioCtx = null;
function _ctx() {
    if (_audioCtx) return _audioCtx;
    try {
        // eslint-disable-next-line no-undef
        const AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) return null;
        _audioCtx = new AC();
    } catch { _audioCtx = null; }
    return _audioCtx;
}

function _beep(frequencies = [880], { duration = 0.14, gainPeak = 0.18, gapSec = 0.06 } = {}) {
    const ctx = _ctx();
    if (!ctx) return;
    let t = ctx.currentTime;
    for (const f of frequencies) {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.value = f;
        gain.gain.setValueAtTime(0, t);
        gain.gain.linearRampToValueAtTime(gainPeak, t + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.0001, t + duration);
        osc.connect(gain).connect(ctx.destination);
        osc.start(t);
        osc.stop(t + duration + 0.02);
        t += duration + gapSec;
    }
}

// Tones — calibrated to feel familiar (client = WhatsApp-ish bright two-tone)
export function playClientPing()   { _beep([880, 1175], { duration: 0.14, gainPeak: 0.22, gapSec: 0.04 }); }
export function playDocumentPing() { _beep([523, 880],  { duration: 0.18, gainPeak: 0.18, gapSec: 0.05 }); }
export function playGeneralPing()  { _beep([660],        { duration: 0.13, gainPeak: 0.13 }); }

// ── Vibration ──
function vibrate(pattern) {
    try { if (navigator.vibrate) navigator.vibrate(pattern); } catch { /* swallow */ }
}

// ── Top-level alert ──
//   role = "client" | "document" | "general"
export function alertChat(role = "general") {
    const prefs = getNotifPrefs();
    if (isInQuietHours(prefs)) return;
    if (prefs.sound) {
        try {
            if (role === "client") playClientPing();
            else if (role === "document") playDocumentPing();
            else playGeneralPing();
        } catch { /* swallow */ }
    }
    if (prefs.vibrate) {
        if (role === "client") vibrate([200, 100, 200]);
        else if (role === "document") vibrate([120, 60, 120, 60, 120]);
        else vibrate([100]);
    }
}

// ── Unlock audio context on the first user gesture (browsers block auto-play) ──
let _unlocked = false;
export function attachAudioUnlock() {
    if (_unlocked) return;
    const unlock = () => {
        const ctx = _ctx();
        if (ctx && ctx.state === "suspended") {
            ctx.resume().catch(() => { /* swallow */ });
        }
        _unlocked = true;
        document.removeEventListener("click", unlock);
        document.removeEventListener("keydown", unlock);
        document.removeEventListener("touchstart", unlock);
    };
    document.addEventListener("click", unlock, { once: false });
    document.addEventListener("keydown", unlock, { once: false });
    document.addEventListener("touchstart", unlock, { once: false });
}
